import React, { useEffect, useState } from 'react';
import {
  Button, Col, Form, Input, InputNumber, Modal, Row, Select, Upload, message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';

const { TextArea } = Input;
const { Option } = Select;

const PRICE_FIELDS = ['avgItemPrice', 'avgProductionPrice', 'avgBrandingPrice', 'avgDeliveryPricePerItemPerKm'];

const PRICE_FIELD_LABELS = {
  avgItemPrice: 'Average Item Price',
  avgProductionPrice: 'Average Production Price',
  avgBrandingPrice: 'Average Branding Price',
  avgDeliveryPricePerItemPerKm: 'Average Delivery Price per Item per Kilometer',
};

export default function SupplierProductModal({
  open,
  onCancel,
  onSubmit,
  editingSupplierProduct = null,
  supplier,
  categories = [],
  products = [],
}) {
  const { t } = useI18n();
  const isEditing = Boolean(editingSupplierProduct);
  const visiblePriceFields = PRICE_FIELDS;

  const [categoryId, setCategoryId] = useState(null);
  const [productId, setProductId] = useState(null);
  const [productName, setProductName] = useState('');
  const [supplierItemNumber, setSupplierItemId] = useState('');
  const [description, setDescription] = useState('');
  const [samplePhotoUrl, setSamplePhotoUrl] = useState(null);
  const [avgItemPrice, setAvgItemPrice] = useState(null);
  const [avgProductionPrice, setAvgProductionPrice] = useState(null);
  const [avgBrandingPrice, setAvgBrandingPrice] = useState(null);
  const [avgDeliveryPricePerItemPerKm, setAvgDeliveryPricePerItemPerKm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCategoryId(editingSupplierProduct?.categoryId ?? null);
    setProductId(editingSupplierProduct?.productId ?? null);
    setProductName(editingSupplierProduct?.productName ?? '');
    setSupplierItemId(editingSupplierProduct?.supplierItemNumber ?? '');
    setDescription(editingSupplierProduct?.description ?? '');
    setSamplePhotoUrl(editingSupplierProduct?.samplePhotoUrl ?? null);
    setAvgItemPrice(editingSupplierProduct?.avgItemPrice ?? null);
    setAvgProductionPrice(editingSupplierProduct?.avgProductionPrice ?? null);
    setAvgBrandingPrice(editingSupplierProduct?.avgBrandingPrice ?? null);
    setAvgDeliveryPricePerItemPerKm(editingSupplierProduct?.avgDeliveryPricePerItemPerKm ?? null);
  }, [open, editingSupplierProduct]);

  const filteredProducts = categoryId ? products.filter((p) => p.categoryId === categoryId) : [];

  const handleCategoryChange = (value) => {
    setCategoryId(value);
    setProductId(null);
  };

  const handleProductChange = (value) => {
    setProductId(value);
    const product = products.find((p) => p.id === value);
    if (product && !productName) {
      setProductName(product.name);
    }
  };

  const handleSubmit = () => {
    if (!categoryId || !productId) {
      message.warning(t('Please select a product category and product'));
      return;
    }
    onSubmit({
      supplierId: supplier.id,
      categoryId,
      productId,
      productName: productName.trim() || products.find((p) => p.id === productId)?.name || '',
      supplierItemNumber: supplierItemNumber.trim(),
      description: description.trim(),
      samplePhotoUrl,
      avgItemPrice: visiblePriceFields.includes('avgItemPrice') ? avgItemPrice : null,
      avgProductionPrice: visiblePriceFields.includes('avgProductionPrice') ? avgProductionPrice : null,
      avgBrandingPrice: visiblePriceFields.includes('avgBrandingPrice') ? avgBrandingPrice : null,
      avgDeliveryPricePerItemPerKm: visiblePriceFields.includes('avgDeliveryPricePerItemPerKm') ? avgDeliveryPricePerItemPerKm : null,
      status: editingSupplierProduct?.status || 'Active',
      ...(isEditing ? { id: editingSupplierProduct.id } : {}),
    });
  };

  const priceFieldSetters = {
    avgItemPrice: setAvgItemPrice,
    avgProductionPrice: setAvgProductionPrice,
    avgBrandingPrice: setAvgBrandingPrice,
    avgDeliveryPricePerItemPerKm: setAvgDeliveryPricePerItemPerKm,
  };
  const priceFieldValues = {
    avgItemPrice,
    avgProductionPrice,
    avgBrandingPrice,
    avgDeliveryPricePerItemPerKm,
  };

  return (
    <Modal
      title={isEditing ? t('Edit Supplier Product') : t('Add Supplier Product')}
      open={open}
      onCancel={onCancel}
      width={680}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Close')}</Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {isEditing ? t('Update') : t('Create')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t('Product Category')} required style={{ marginBottom: 12 }}>
              <Select
                placeholder={t('Select category')}
                value={categoryId ?? undefined}
                onChange={handleCategoryChange}
                showSearch
                optionFilterProp="children"
              >
                {categories.filter((c) => c.status !== 'Inactive').map((category) => (
                  <Option key={category.id} value={category.id}>{category.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t('Product')} required style={{ marginBottom: 12 }}>
              <Select
                placeholder={categoryId ? t('Select product') : t('Select category first')}
                value={productId ?? undefined}
                onChange={handleProductChange}
                disabled={!categoryId}
                showSearch
                optionFilterProp="children"
              >
                {filteredProducts.map((product) => (
                  <Option key={product.id} value={product.id}>{product.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t('Product Name')} style={{ marginBottom: 12 }}>
              <Input
                placeholder={t('Supplier-specific product name')}
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t('Supplier Item Number')} style={{ marginBottom: 12 }}>
              <Input
                placeholder={t('Supplier article / SKU')}
                value={supplierItemNumber}
                onChange={(e) => setSupplierItemId(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('Description')} style={{ marginBottom: 12 }}>
          <TextArea
            rows={2}
            placeholder={t('Item description / characteristics')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>

        <Form.Item label={t('Sample Photo')} style={{ marginBottom: 12 }}>
          {samplePhotoUrl && (
            <img
              src={samplePhotoUrl}
              alt={t('Sample Photo')}
              style={{ display: 'block', maxWidth: 160, maxHeight: 160, marginBottom: 8, borderRadius: 6 }}
            />
          )}
          <Upload
            accept="image/*"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              setSamplePhotoUrl(URL.createObjectURL(file));
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>{t('Upload Photo')}</Button>
          </Upload>
        </Form.Item>

        <Row gutter={16}>
          {visiblePriceFields.map((field) => (
            <Col xs={24} sm={12} key={field}>
              <Form.Item label={t(PRICE_FIELD_LABELS[field])} style={{ marginBottom: 12 }}>
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder={t('Not provided')}
                  value={priceFieldValues[field]}
                  onChange={(value) => priceFieldSetters[field](value ?? null)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>
    </Modal>
  );
}
